import React, { HTMLProps } from 'react';
import { CheckboxProps } from '@givebutter/primitives/checkbox';
import { Switch } from '@givebutter/primitives/switch';
import { useCustomField } from 'components/custom-fields/CustomFieldForm';
import { toFriendlyType } from 'components/custom-fields/helpers';
import * as Form from 'components/form';
import { twJoin, twMerge } from 'tailwind-merge';

interface DateFormSectionProps {
    children?: React.ReactNode;
}

const CustomFieldDateForm = ({ children }: DateFormSectionProps) => {
    const methods = Form.useGivebutterFormContext();
    const { setValue, watch, getValues } = methods;
    const { customField, getFormFieldPath } = useCustomField();

    const typePath = getFormFieldPath('type');

    const watchType = watch(typePath);

    const type = getValues(typePath);
    if (toFriendlyType(type) !== 'date') {
        return null;
    }

    const isEditing = Boolean(customField);

    const toggleDateRange: CheckboxProps['onCheckedChange'] = isChecked => {
        setValue(typePath, isChecked ? 'daterange' : 'date');
    };

    return (
        <Form.Container className={'flex flex-col gap-y-1.5'}>
            <div className={twJoin('flex flex-col gap-y-1', isEditing && 'opacity-50')}>
                <div className={'flex justify-between'}>
                    <Form.Label
                        name={'is_daterange'}
                        className={isEditing ? 'cursor-not-allowed' : 'cursor-pointer'}
                    >
                        Date Range
                    </Form.Label>
                    <Switch
                        id={'is_daterange'}
                        onCheckedChange={toggleDateRange}
                        defaultChecked={watchType === 'daterange'}
                        checked={watchType === 'daterange'}
                        disabled={isEditing}
                    />
                </div>
                {children || (
                    <DateRangeDescription>
                        When enabled, you can select a start and end date from the calendar picker.
                    </DateRangeDescription>
                )}
            </div>
            <Form.UseFormError name={typePath} />
        </Form.Container>
    );
};

const DateRangeDescription = (props: HTMLProps<HTMLParagraphElement>) => {
    const { className = '', ...rest } = props;

    return <p className={twMerge('text-sm text-gray-600', className)} {...rest} />;
};

const DateForm = CustomFieldDateForm;

export {
    CustomFieldDateForm,
    DateRangeDescription,
    //
    DateForm,
};
